'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { CloseButton } from '../close-button/close-button';
import { Slot } from '../slot';
import { PopoverRootContext } from './context/popover-root.context';
import { popoverCloseButtonStyles } from './styles/popover-close-button.styles';
import type { PopoverCloseButtonPropsInterface } from './types/popover-close-button.types';

/**
 * @since 10.0.0
 */
export const PopoverCloseButton = forwardRef<HTMLButtonElement, PopoverCloseButtonPropsInterface>(
  (props: PopoverCloseButtonPropsInterface, forwardedRef) => {
    const { children = <CloseButton />, className, css: cssProp, onClick, ...rest } = props;
    const { popover } = useContext(PopoverRootContext);

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        popover.onClickClose(event);
        onClick?.(event);
      },
      [popover, onClick],
    );
    const isNativeElement = typeof children.type === 'string';

    return (
      <Slot
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Popover.CloseButton"
        {...(isNativeElement
          ? {
              className: cx(
                'c11n_popover__close-button',
                css(popoverCloseButtonStyles, cssProp),
                className,
              ),
            }
          : {
              css: { ...popoverCloseButtonStyles, ...cssProp },
              className: cx('c11n_popover__close-button', className),
            })}
        {...rest}
        onClick={onClickHandler}
      >
        {children}
      </Slot>
    );
  },
);

PopoverCloseButton.displayName = 'Popover.CloseButton';
